import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { bookmakerApi, bmErrorMessage } from '@/api/bookmaker'
import { getLabUserId } from '@/config/lab'
import { BmForm, BmPage } from './BmPage'

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

type UserFormValues = {
  userName: string
  email: string
  password: string
  confirmPassword: string
  status: string
}

export function CreateUserPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [values, setValues] = useState<UserFormValues>({
    userName: '',
    email: '',
    password: '',
    confirmPassword: '',
    status: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!id) return
    void (async () => {
      setBusy(true)
      setError(null)
      try {
        const raw = asRecord(await bookmakerApi.getOneUser({ userId: id, _id: id }))
        const data = asRecord(raw.value === true || raw.data ? raw.data : raw)
        setValues({
          userName: String(data.userName ?? data.username ?? ''),
          email: String(data.email ?? ''),
          password: '',
          confirmPassword: '',
          status: String(data.status ?? 'Enable'),
        })
        if (raw.value === false) {
          setError(String(raw.msg ?? 'User not found'))
        }
      } catch (e) {
        setError(bmErrorMessage(e))
      } finally {
        setBusy(false)
      }
    })()
  }, [id])

  async function onSubmit() {
    setBusy(true)
    setError(null)
    try {
      if (!values.userName.trim() || !values.email.trim() || !values.status) {
        setError('Username, email and status are required')
        return
      }
      if (!isEdit || values.password || values.confirmPassword) {
        if (values.password !== values.confirmPassword) {
          setError('Password and confirm password should be same !')
          return
        }
        if (!isEdit && !values.password) {
          setError('Password is required')
          return
        }
      }

      const parent = getLabUserId()
      if (isEdit) {
        const body: Record<string, unknown> = {
          _id: id,
          userName: values.userName.trim(),
          email: values.email.trim(),
          status: values.status,
          level: 'User',
          parent: parent || undefined,
          password: values.password,
          confirmPassword: values.confirmPassword,
        }
        const res = asRecord(await bookmakerApi.updateOneUser(body))
        if (res.value === false) {
          setError(String(res.msg ?? 'Error while saving username or email already exist..'))
          return
        }
      } else {
        const res = asRecord(
          await bookmakerApi.createUser({
            userName: values.userName.trim(),
            email: values.email.trim(),
            password: values.password,
            confirmPassword: values.confirmPassword,
            status: values.status,
            parent: parent || undefined,
          }),
        )
        if (res.value === false) {
          setError(String(res.msg ?? 'Error while saving username or email already exist..'))
          return
        }
      }
      navigate('/user')
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title={isEdit ? 'Edit User' : 'Create User'}
      description="Lab user create/edit — no role; status Enable/Disable"
      error={error}
    >
      <BmForm
        fields={[
          { key: 'userName', label: 'Username', placeholder: 'Username' },
          { key: 'email', label: 'Email', type: 'email', placeholder: 'Email' },
          {
            key: 'password',
            label: 'Password',
            type: 'password',
            placeholder: isEdit ? 'Leave blank to keep' : 'Password',
          },
          {
            key: 'confirmPassword',
            label: 'Confirm Password',
            type: 'password',
            placeholder: 'Confirm Password',
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            placeholder: '--Please choose a Status--',
            options: [
              { value: 'Enable', label: 'Enable' },
              { value: 'Disable', label: 'Disable' },
            ],
          },
        ]}
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
        onSubmit={() => void onSubmit()}
        busy={busy}
        submitLabel={isEdit ? 'Edit' : 'Create'}
      />
    </BmPage>
  )
}
